import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, MessagesSquare, ShieldAlert, X } from 'lucide-react';
import { api } from '../../lib/api';
import type { ChatbotConversationSummary, ChatbotMessageRecord } from '../../lib/types';
import { chatbotFade, formatChatbotDate } from './chatbotShared';

export default function ChatbotConversationsTab() {
  const [list, setList] = useState<ChatbotConversationSummary[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.chatbot.conversations().then(setList).catch(() => setError('No se pudieron cargar las conversaciones.'));
  }, []);

  if (error) return <p className="text-sm text-ui-danger">{error}</p>;
  if (!list) return <div className="flex items-center gap-2 text-sm text-ui-text-subtle"><Loader2 size={15} className="animate-spin" /> Cargando…</div>;
  if (list.length === 0) return <p className="text-sm text-ui-text-subtle">Todavía no hay conversaciones con el asistente.</p>;

  return (
    <motion.div {...chatbotFade} className="space-y-2">
      {list.map((conversation) => (
        <button
          key={conversation.id}
          type="button"
          onClick={() => setOpenId(conversation.id)}
          className="w-full flex items-center gap-3 rounded-lg border border-ui-border bg-ui-surface hover:bg-ui-surface-2 px-4 py-3 text-left transition-colors"
        >
          <MessagesSquare size={16} className="text-ui-text-subtle flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-ui-text">{formatChatbotDate(conversation.startedAt)}</p>
            <p className="text-sm text-ui-text-subtle">{conversation.messageCount} mensajes · {conversation.locale.toUpperCase()}</p>
          </div>
          {conversation.flagged && (
            <span className="flex items-center gap-1 text-sm font-medium text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">
              <ShieldAlert size={13} /> Revisar
            </span>
          )}
        </button>
      ))}
      {openId && <TranscriptModal id={openId} onClose={() => setOpenId(null)} />}
    </motion.div>
  );
}

function TranscriptModal({ id, onClose }: { id: string; onClose: () => void }) {
  const [messages, setMessages] = useState<ChatbotMessageRecord[] | null>(null);

  useEffect(() => {
    api.chatbot.conversation(id).then((response) => setMessages(response.messages)).catch(() => setMessages([]));
  }, [id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-lg max-h-[80vh] flex flex-col bg-ui-surface border border-ui-border rounded-lg overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-ui-border">
          <p className="text-sm font-medium text-ui-text">Conversación</p>
          <button type="button" onClick={onClose} className="text-ui-text-subtle hover:text-ui-text"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {!messages ? (
            <div className="flex items-center gap-2 text-sm text-ui-text-subtle"><Loader2 size={15} className="animate-spin" /> Cargando…</div>
          ) : messages.length === 0 ? (
            <p className="text-sm text-ui-text-subtle">Sin mensajes.</p>
          ) : (
            messages.map((message, index) => <TranscriptLine key={index} message={message} />)
          )}
        </div>
      </motion.div>
    </div>
  );
}

function TranscriptLine({ message }: { message: ChatbotMessageRecord }) {
  if (message.role === 'tool') {
    return (
      <p className="text-sm text-ui-text-subtle italic px-2">
        🔧 Consultó: {message.toolName ?? 'datos del negocio'}
      </p>
    );
  }

  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${isUser ? 'bg-ui-accent/15 text-ui-text' : 'bg-ui-bg text-ui-text-muted'}`}>
        {message.flagged && <span className="block text-sm text-amber-400 mb-1">Posible intento de manipulación</span>}
        {message.content}
      </div>
    </div>
  );
}
